import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, FileText, Inbox, Newspaper } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("ghp-admin-token")}` });

const DashboardTab = ({ go }) => {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/stats`, { headers: authHeader() })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  if (!stats) {
    return <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-graphite-soft">…</p>;
  }

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" });

  const cards = [
    { label: t("admin.statsContacts"), value: stats.submissions_total, tab: "submissions" },
    { label: t("admin.statsNetwork"), value: stats.submissions_by_type.network || 0, tab: "submissions" },
    { label: t("admin.statsPartners"), value: stats.submissions_by_type.partner || 0, tab: "submissions" },
    { label: t("admin.statsNewsletter"), value: stats.newsletter_count, tab: "newsletter" },
    { label: t("admin.statsNews"), value: stats.news_count, tab: "news" },
    { label: t("admin.statsDocs"), value: stats.docs_count, tab: "docs" },
  ];

  return (
    <div data-testid="admin-dashboard-tab">
      <div className="grid grid-cols-2 gap-px bg-graphite/10 md:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => go(c.tab)}
            className="group bg-bone p-6 text-left transition-colors duration-300 hover:bg-forest"
            data-testid={`stat-card-${c.tab}-${c.label.slice(0, 8)}`}
          >
            <p className="font-display text-4xl font-light tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
              {c.value}
            </p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
              {c.label}
            </p>
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        <div className="border border-graphite/10 bg-white p-6">
          <p className="overline-mono flex items-center gap-2 text-forest">
            <Inbox className="h-4 w-4" />
            {t("admin.recentSubmissions")}
          </p>
          <div className="mt-5 space-y-3">
            {stats.recent_submissions.length === 0 && <p className="text-sm text-graphite-soft">{t("admin.nothingHere")}</p>}
            {stats.recent_submissions.map((s) => (
              <div key={s.id} className="border-b border-graphite/10 pb-3 last:border-0">
                <p className="text-sm font-medium text-graphite">{s.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-graphite-soft">
                  {t(`adminTypes.${s.type}`)} — {fmtDate(s.created_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-graphite/10 bg-white p-6">
          <p className="overline-mono flex items-center gap-2 text-forest">
            <CalendarDays className="h-4 w-4" />
            {t("admin.upcomingEvents")}
          </p>
          <div className="mt-5 space-y-3">
            {stats.upcoming_events.length === 0 && <p className="text-sm text-graphite-soft">{t("admin.nothingHere")}</p>}
            {stats.upcoming_events.map((e) => (
              <div key={e.id} className="border-b border-graphite/10 pb-3 last:border-0">
                <p className="text-sm font-medium text-graphite">{e.title_fr}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-graphite-soft">{fmtDate(e.date)}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-graphite/10 bg-white p-6">
          <p className="overline-mono flex items-center gap-2 text-forest">
            <Newspaper className="h-4 w-4" />
            {t("admin.latestNews")}
          </p>
          <div className="mt-5 space-y-3">
            {stats.recent_news.length === 0 && <p className="text-sm text-graphite-soft">{t("admin.nothingHere")}</p>}
            {stats.recent_news.map((n) => (
              <div key={n.id} className="border-b border-graphite/10 pb-3 last:border-0">
                <p className="text-sm font-medium text-graphite">{n.title_fr}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-graphite-soft">
                  {t(`newsCats.${n.category}`)} — {fmtDate(n.created_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 hidden">
        <FileText className="h-4 w-4" />
      </div>
    </div>
  );
};

export default DashboardTab;
